import { useBunja } from 'bunja/react';
import { useTranslations } from 'next-intl';
import { MouseEventHandler } from 'react';

import { useSnapshotSpec } from '@flipster/features/src/shared/snapshot/use-snapshot-spec';
import { OpenPosition } from '@flipster/r230522/original/position';
import { getQuoteCurrencyPrecision } from '@flipster/utils/constants/currency';
import Num, { BigNumber, numberFormatter } from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { PageType, PositionEvent, PositionType } from '@flipster/utils/segment/events/position';

import PriceRateButtons from '@web/components/PriceRateButtons';

import { adjustMarginStateBunja } from '../state';
import styles from './inputForm.module.scss';

interface Props {
  maxAmount: string;
  currentPosition: OpenPosition;
  page: PageType;
}

const AdjustMarginButtons = ({ maxAmount, currentPosition, page }: Props) => {
  const t = useTranslations();
  const { setAmount } = useBunja(adjustMarginStateBunja);
  const { instrumentsKeyBySymbol } = useSnapshotSpec();

  const instrument = instrumentsKeyBySymbol?.[currentPosition.symbol];
  if (!instrument) return null;
  const quoteCurrencyPrecision = getQuoteCurrencyPrecision(instrument.quoteCurrency);

  const rateItems = [
    { label: '25%', value: '25' },
    { label: '50%', value: '50' },
    { label: '75%', value: '75' },
    {
      label: (
        <div>
          <p>100%</p>
          <p className={styles.caption}>
            {t('Position_add_funds.r250828.Max_amount_quote_currency', {
              amount: numberFormatter(maxAmount, {
                withComma: true,
                precision: quoteCurrencyPrecision,
              }),
              quoteCurrency: instrument?.quoteCurrency,
            })}
          </p>
        </div>
      ),
      value: '100',
    },
  ];

  const onClickPercent: MouseEventHandler<HTMLButtonElement> = (e) => {
    const { value } = e.currentTarget;

    const calculatedAmount = Num(maxAmount)
      .dividedBy(100)
      .multipliedBy(value)
      .toFixed(quoteCurrencyPrecision, BigNumber.ROUND_HALF_UP);

    setAmount(calculatedAmount);

    if (!currentPosition) return;
    segment.track(PositionEvent.AdjustMarginPercentageClick, {
      margin_usdt: Number(currentPosition.fundsInvested),
      symbol: currentPosition.symbol,
      unrealized_pnl: Number(currentPosition.unrealizedPnl),
      leverage: currentPosition.initLeverage,
      trade_type: 'perpetual', // spot 적용 시 수정해야함.
      page_type: page,
      position: currentPosition.side.toLowerCase() as PositionType,
      percentage: Number(value) * 100,
    });
  };

  return (
    <div className={styles.buttonWrapper}>
      <PriceRateButtons items={rateItems} onClick={onClickPercent} className={styles.priceButton} />
    </div>
  );
};

export default AdjustMarginButtons;
